import { useMemo, memo } from 'react';
import {
  VirtualizedSearchSelect,
  SearchSelectItem,
} from '../search-select/VirtualizedSearchSelect';
import { ObjectTypeLabelAndKeyTag } from '../ObjectTypeLabelAndKey';
import { highlightMatch } from '../../../lib/highlightMatch';
import useObjectTypesWithFetch, {
  ObjectTypePropertyFilterType,
  objectTypesPropertyFilter,
} from '../../../hooks/useObjectTypesWithFetch';
import { Box } from '@mui/material';

interface SweepObjectSelectorProps {
  crmOrgId: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  autoOpen?: boolean;
  fullWidth?: boolean;
  filterBy?: (objectType: ObjectTypeName) => boolean;
  objectTypeFilters?: ObjectTypePropertyFilterType;
  placeholder?: string;
}

const promotedObjectTypes = ['Lead', 'Account', 'Opportunity', 'Contact', 'Case'];

const emptyArray: ObjectTypeName[] = [];

const SweepObjectSelectorComponent = ({
  crmOrgId,
  onChange,
  value = '',
  disabled,
  fullWidth,
  filterBy,
  objectTypeFilters,
  autoOpen,
  placeholder = 'e.g Lead, Opportunity',
}: SweepObjectSelectorProps) => {
  const { objectTypes: _objectTypes = emptyArray } = useObjectTypesWithFetch({ crmOrgId });

  const objectTypes = useMemo(() => {
    const __objectTypes = objectTypesPropertyFilter({
      objectTypes: _objectTypes,
      filters: objectTypeFilters || {},
    }) as ObjectTypeName[];
    if (!filterBy) {
      return __objectTypes;
    }
    return __objectTypes.filter((ot) => value === ot.objectType || filterBy(ot));
  }, [_objectTypes, filterBy, objectTypeFilters, value]);

  const objectTypesSet = useMemo(
    () => new Set(objectTypes.map(({ objectType }) => objectType)),
    [objectTypes],
  );

  const allObjects = objectTypes.filter(
    (objectType) => !promotedObjectTypes.includes(objectType.objectType),
  );

  const items = useMemo(() => {
    const promotedItems: SearchSelectItem<ObjectTypeName>[] = promotedObjectTypes
      .filter((objectType) => objectTypesSet.has(objectType))
      .map((objectType) => ({
        label: objectType,
        value: objectType,
      }));
    const allItems: SearchSelectItem<ObjectTypeName>[] = allObjects.map((objectType) => ({
      label: objectType.label,
      value: objectType.objectType,
    }));

    const items: SearchSelectItem<ObjectTypeName>[] = [...promotedItems, ...allItems];

    return items;
  }, [allObjects, objectTypesSet]);

  return (
    <VirtualizedSearchSelect
      items={items}
      onChange={(item) => onChange && onChange(item.value)}
      SelectProps={{
        value: value || '',
        placeholder,
      }}
      FormControlProps={{
        fullWidth,
      }}
      dataTestId="sweep-object-selector"
      itemRenderer={(item, search) => (
        <Box width="fit-content">
          <ObjectTypeLabelAndKeyTag
            objectType={item.label}
            label={highlightMatch(item.label, search)}
          />
        </Box>
      )}
      disabled={disabled}
      autoOpen={autoOpen}
      itemHeight={36}
      maxHeight={400}
    />
  );
};

// Custom comparison function for memo
const arePropsEqual = (
  prevProps: SweepObjectSelectorProps,
  nextProps: SweepObjectSelectorProps,
) => {
  return (
    prevProps.crmOrgId === nextProps.crmOrgId &&
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.autoOpen === nextProps.autoOpen &&
    prevProps.fullWidth === nextProps.fullWidth &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.filterBy === nextProps.filterBy &&
    JSON.stringify(prevProps.objectTypeFilters) === JSON.stringify(nextProps.objectTypeFilters)
  );
};

export const SweepObjectSelector = memo(SweepObjectSelectorComponent, arePropsEqual);
